<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useSideMenu } from '@/composables/useSideMenu'
import { h } from 'vue'
import IconRenderer from './IconRenderer.vue'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  FolderOutlined,
  MenuOutlined,
  UserOutlined,
  BellOutlined,
  DownOutlined,
  LogoutOutlined
} from '@ant-design/icons-vue'

// Props
interface Props {
  collapsed?: boolean
  onCollapseChange?: (collapsed: boolean) => void
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false
})

// Emits
const emit = defineEmits<{
  'update:collapsed': [value: boolean]
  'menu-click': [key: string, item: any]
}>()

// 路由和状态管理
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const { loading, filteredMenuItems, loadMenuData } = useSideMenu()

// 响应式状态
const selectedKeys = ref<string[]>(['dashboard'])
const openKeys = ref<string[]>([])
const menuRef = ref()

// 获取菜单图标组件
const getMenuIcon = (iconName?: string, iconColor?: string) => {
  if (!iconName) {
    return undefined // 不显示默认图标
  }
  
  // 使用 IconRenderer 组件来渲染图标
  return () => h(IconRenderer, {
    icon: iconName,
    iconColor: iconColor || '#FFF',
    size: 16
  })
}

// 转换菜单数据为Ant Design菜单格式
const menuItems = computed(() => {
  const items = filteredMenuItems.value.map(item => {
    const menuItem = {
      key: item.key,
      icon: getMenuIcon(item.icon, item.iconColor),
      label: item.label,
      children: item.children?.map((child: any) => {
        const childItem = {
          key: child.key,
          icon: getMenuIcon(child.icon, child.iconColor),
          label: child.label,
          children: child.children
        }
        return childItem
      })
    }
    return menuItem
  })
  return items
})

// 菜单点击处理
const handleMenuClick = ({ key, keyPath }: { key: string; keyPath: string[] }) => {
  selectedKeys.value = [key]
  emit('menu-click', key, { key, keyPath })
  
  // 路由跳转
  const targetPath = `/${key}`
  
  try {
    router.push(targetPath).then(() => {
    }).catch((error) => {
    })
  } catch (error) {
  }
}

// 菜单选择处理
const handleMenuSelect = ({ key, keyPath }: { key: string; keyPath: string[] }) => {
  selectedKeys.value = [key]
  emit('menu-click', key, { key, keyPath })
  
  // 路由跳转
  const targetPath = `/${key}`
  
  try {
    router.push(targetPath).then(() => {
    }).catch((error) => {
      console.error('❌ 路由跳转失败:', error)
    })
  } catch (error) {
    console.error('❌ 路由跳转异常:', error)
  }
}

// 菜单取消选择处理
const handleMenuDeselect = ({ key, keyPath }: { key: string; keyPath: string[] }) => {
  // 当取消选择时，确保selectedKeys只包含当前路由的key
  const currentPathKey = route.path.replace('/', '') || 'dashboard'
  if (keyPath.length === 1 && keyPath[0] === currentPathKey) {
    selectedKeys.value = []
  } else {
    selectedKeys.value = [currentPathKey]
  }
}

// 折叠/展开处理
const toggleCollapsed = () => {
  const newCollapsed = !props.collapsed
  emit('update:collapsed', newCollapsed)
}

// 监听路由变化，更新选中状态
watch(() => route.path, (newPath) => {
  const pathKey = newPath.replace('/', '') || 'dashboard'
  selectedKeys.value = [pathKey]
}, { immediate: true })

// 监听用户权限变化，重新过滤菜单
watch(() => userStore.userPermissions, () => {
}, { deep: true })

// 组件挂载时加载菜单数据
onMounted(() => {
  loadMenuData()
})
</script>

<template>
  <div class="side-menu">
    <!-- Logo区域 -->
    <div class="logo">
      <div style="display: flex; align-items: center; justify-content: space-between; padding: 0 16px;">
        <h2 style="color: white; text-align: center; margin: 16px 0; flex: 1;">
          {{ collapsed ? 'WM' : '营销平台' }}
        </h2>
        <a-button
          type="text"
          @click="toggleCollapsed"
          style="color: white; border: none; padding: 4px;"
        >
          <MenuUnfoldOutlined v-if="collapsed" />
          <MenuFoldOutlined v-else />
        </a-button>
      </div>
    </div>
    
    <!-- 菜单区域 -->
    <div class="menu-container">
      <!-- 菜单加载中 -->
      <div v-if="loading" class="menu-loading">
        <a-spin size="small" />
        <span style="color: #fff; margin-left: 8px;">加载菜单中...</span>
      </div>
      
      <!-- 菜单为空时的提示 -->
      <div v-else-if="menuItems.length === 0" class="menu-empty">
        <div style="color: #8c8c8c; text-align: center; padding: 20px;">
          <div style="margin-bottom: 8px;">📋</div>
          <div>暂无菜单数据</div>
          <div style="font-size: 12px; margin-top: 4px;">请联系管理员配置菜单权限</div>
        </div>
      </div>
      
      <!-- 菜单列表 -->
      <a-menu
        v-else
        v-model:selectedKeys="selectedKeys"
        v-model:openKeys="openKeys"
        theme="dark"
        mode="inline"
        :items="menuItems"
        @click="handleMenuClick"
        @select="handleMenuSelect"
        @deselect="handleMenuDeselect"
        style="flex: 1;"
        ref="menuRef"
      />
    </div>
    
    <!-- 底部用户信息区域 -->
    <div class="user-section" :class="{ collapsed }">
      <!-- 通知区域 -->
      <div class="notification-section">
        <a-badge :count="3" :offset="[-5, 5]">
          <a-button 
            type="text" 
            style="color: white; width: 100%; text-align: center;"
          >
            <BellOutlined />
            <span v-if="!collapsed" style="margin-left: 8px;">通知</span>
          </a-button>
        </a-badge>
      </div>
      
      <!-- 用户信息 -->
      <div class="user-info">
        <a-dropdown placement="topRight">
          <div class="user-avatar">
            <a-avatar :size="collapsed ? 32 : 40" style="background: #1890ff;">
              <UserOutlined />
            </a-avatar>
            <div v-if="!collapsed" class="user-details">
              <div class="user-name">{{ userStore.userName || '平台管理员' }}</div>
              <div class="user-role">{{ userStore.userRoles[0] || '超级管理员' }}</div>
            </div>
            <DownOutlined v-if="!collapsed" style="color: #bfbfbf; margin-left: 8px;" />
          </div>
          <template #overlay>
            <a-menu>
              <a-menu-item key="profile">
                <UserOutlined />
                个人资料
              </a-menu-item>
              <a-menu-item key="settings">
                <SettingOutlined />
                系统设置
              </a-menu-item>
              <a-menu-divider />
              <a-menu-item key="logout">
                <LogoutOutlined />
                退出登录
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </div>
    </div>
  </div>
</template>

<style scoped>
.side-menu {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #001529;
}

.logo {
  height: 32px;
  margin: 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  flex-shrink: 0;
}

.menu-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.menu-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.menu-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.user-section {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.2);
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.user-section.collapsed {
  padding: 8px;
}

.notification-section {
  margin-bottom: 8px;
}

.user-info {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 8px;
}

.user-avatar {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.user-avatar:hover {
  background: rgba(255, 255, 255, 0.1);
}

.user-details {
  margin-left: 8px;
  flex: 1;
}

.user-name {
  color: white;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.2;
}

.user-role {
  color: #bfbfbf;
  font-size: 12px;
  line-height: 1.2;
}

:deep(.ant-menu) {
  border-right: none;
}

:deep(.ant-menu-item) {
  margin: 0;
}

:deep(.ant-menu-submenu-title) {
  margin: 0;
}

:deep(.ant-menu-dark .ant-menu-item-selected) {
  background-color: #1890ff;
}

/* 去掉菜单hover时的背景色改变 */
:deep(.ant-menu-dark .ant-menu-item:hover) {
  background-color: transparent !important;
}

:deep(.ant-menu-dark .ant-menu-submenu-title:hover) {
  background-color: transparent !important;
}

:deep(.ant-dropdown-menu) {
  min-width: 120px;
}
</style> 